// src/services/useAuthStore.js
import { create } from "zustand";
import { api, setCurrentWorkspace, getCurrentWorkspace, clearCurrentWorkspace } from "./api";

export const useAuthStore = create((set, get) => ({
  user: null,
  workspace: null,      // Workspace ativo atualmente
  workspaces: [],       // Todos os workspaces disponiveis
  membership: null,
  modules: [],
  preferences: null,
  subscription: null,
  isAuthenticated: false,
  status: "idle",       // "idle" | "loading" | "authenticated" | "unauthenticated"
  lastError: null,

  /**
   * Define auth a partir da resposta de login.
   * Usado apos POST /api/auth/login.
   */
  setAuthFromLogin: (loginData) =>
    set(() => {
      const safeData = loginData || {};
      const workspaces = safeData.workspaces || [];
      const defaultWs = safeData.default_workspace || (workspaces.length > 0 ? workspaces[0] : null);

      // Definir workspace no api.js
      if (defaultWs) {
        setCurrentWorkspace(defaultWs.id);
      }

      return {
        user: safeData.user || null,
        workspaces: workspaces,
        workspace: defaultWs || null,
        membership: null,  // Sera preenchido via switchWorkspace
        modules: [],       // Sera preenchido via switchWorkspace
        subscription: null,
        isAuthenticated: !!safeData.user,
        status: "authenticated",
        lastError: null,
      };
    }),

  /**
   * Define auth a partir da resposta de /me.
   * Usado na inicializacao e apos switchWorkspace.
   */
  setAuthFromMe: (meData) =>
    set(() => {
      const safeData = meData || {};
      const hasUser = !!safeData.user;

      // Atualizar workspace no api.js
      if (safeData.workspace?.id) {
        setCurrentWorkspace(safeData.workspace.id);
      }

      return {
        user: safeData.user || null,
        workspace: safeData.workspace || null,
        workspaces: safeData.workspaces || [],
        membership: safeData.membership || null,
        modules: safeData.modules || [],
        preferences: safeData.preferences || null,
        subscription: safeData.subscription || null,
        isAuthenticated: hasUser,
        status: hasUser ? "authenticated" : "unauthenticated",
        lastError: null,
      };
    }),

  /**
   * Troca para outro workspace.
   * Chama /api/auth/switch-workspace e atualiza o estado.
   */
  switchWorkspace: async (workspaceId) => {
    const { workspaces } = get();

    // Validar se usuario tem acesso
    const targetWs = workspaces.find(ws => ws.id === workspaceId);
    if (!targetWs) {
      throw new Error("Workspace not available");
    }

    try {
      // Atualizar header antes de chamar API
      setCurrentWorkspace(workspaceId);

      // Chamar endpoint de switch
      const response = await api.post("/api/auth/switch-workspace", {
        workspace_id: workspaceId
      });

      // Atualizar estado com dados do novo workspace
      set({
        workspace: response.data.workspace,
        membership: response.data.membership,
        modules: response.data.modules,
        subscription: response.data.subscription,
      });

      return response.data;

    } catch (err) {
      // Em caso de erro, reverter para workspace anterior
      const currentWs = get().workspace;
      if (currentWs) {
        setCurrentWorkspace(currentWs.id);
      }
      throw err;
    }
  },

  /**
   * Limpa todo o estado de autenticacao.
   * Usado no logout.
   */
  clearAuth: () => {
    clearCurrentWorkspace();
    set(() => ({
      user: null,
      workspace: null,
      workspaces: [],
      membership: null,
      modules: [],
      preferences: null,
      subscription: null,
      isAuthenticated: false,
      status: "unauthenticated",
      lastError: null,
    }));
  },

  /**
   * Inicializa autenticacao a partir de /me.
   * Usado no carregamento inicial do app.
   */
  initAuthFromMe: async () => {
    const { status, setAuthFromMe, clearAuth, switchWorkspace } = get();

    if (status === "loading") return;

    set({ status: "loading", lastError: null });

    try {
      // Recuperar workspace do localStorage
      const savedWorkspaceId = getCurrentWorkspace();

      // Chamar /me (se tiver workspace salvo, header sera enviado)
      const response = await api.get("/api/auth/me");
      setAuthFromMe(response.data || {});

      // Se tinhamos um workspace salvo diferente do retornado, tentar trocar
      const responseWorkspaces = response.data.workspaces || [];
      if (savedWorkspaceId && responseWorkspaces.length > 0) {
        const wsExists = responseWorkspaces.find(
          ws => ws.id === savedWorkspaceId
        );
        if (wsExists && wsExists.id !== response.data.workspace?.id) {
          // Trocar para o workspace salvo
          try {
            await switchWorkspace(savedWorkspaceId);
          } catch (switchErr) {
            // Se falhar, manter o workspace retornado pelo /me
            console.warn("Failed to switch to saved workspace:", switchErr);
          }
        }
      }

    } catch (err) {
      console.warn("Falha ao carregar /me (sessao expirada ou erro):", err);
      clearAuth();
      set({ lastError: err });
    }
  },

  // ========== Module Helpers ==========

  hasModule: (moduleKey) => {
    const modules = get().modules || [];
    return modules.some((m) => m.module === moduleKey);
  },

  canViewModule: (moduleKey) => {
    const modules = get().modules || [];
    const found = modules.find((m) => m.module === moduleKey);
    return !!(found && found.can_view);
  },

  canEditModule: (moduleKey) => {
    const modules = get().modules || [];
    const found = modules.find((m) => m.module === moduleKey);
    return !!(found && found.can_edit);
  },

  // ========== Subscription Helpers ==========

  isSubscriptionActive: () => {
    const subscription = get().subscription;
    return subscription?.is_active === true;
  },

  needsPayment: () => {
    const subscription = get().subscription;
    if (!subscription) return true;
    return subscription.status === 'pending_payment' || subscription.status === 'canceled';
  },

  getCheckoutUrl: () => {
    const user = get().user;
    const workspace = get().workspace;

    const baseUrl = process.env.REACT_APP_CHECKOUT_URL || 'https://pay.kiwify.com.br/seu-produto';

    const params = new URLSearchParams();
    if (user?.name) params.set('name', user.name);
    if (user?.email) params.set('email', user.email);
    if (workspace?.slug) params.set('ref', workspace.slug);

    return `${baseUrl}?${params.toString()}`;
  },
}));
